import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import CssBaseline from "@mui/material/CssBaseline";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useNavigate } from "react-router";

import { ErrorBoundary, ErrorHandlerProvider } from "@dfp/react-api-core";
import { DevBar, SnackbarProvider } from "@dfp/react-components";
import {
  LokaliseInContextEditor,
  useLokaliseEditor,
} from "@dfp/react-lokalise";
import { MsalAccountProvider } from "@dfp/react-msal-support";
import { CustomNavigationClient } from "@dfp/react-msal-support";

import { AppRoutes } from "./AppRoutes.tsx";
import { QueryClientProviderBridge } from "./QueryClientProviderBridge.tsx";
import {
  API_ENDPOINT_PREFIX,
  COMMIT_ID,
  DEMO_MODE,
  DEV_BAR_COLOR,
  LOKALISE_IN_CONTEXT_EDITOR_ENABLED,
  LOKALISE_PROJECT_ID,
} from "./config.ts";
import { enableLokalisePostProcessor } from "./i18n/initI18next.ts";

type Props = {
  pca: PublicClientApplication;
};

function App({ pca }: Props) {
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  pca.setNavigationClient(navigationClient);

  const { editorEnabled, devBarActions } = useLokaliseEditor({
    enabled: LOKALISE_IN_CONTEXT_EDITOR_ENABLED,
    onEditorEnable: enableLokalisePostProcessor,
  });

  return (
    <ErrorBoundary>
      <MsalProvider instance={pca}>
        <MsalAccountProvider>
          {/* Dev-only debug bar — hidden in the demo (public preview). */}
          {!DEMO_MODE && (
            <DevBar
              color={DEV_BAR_COLOR}
              commitId={COMMIT_ID}
              apiEndpointPrefix={API_ENDPOINT_PREFIX}
              actions={devBarActions}
            />
          )}
          <SnackbarProvider
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <ErrorHandlerProvider>
              <QueryClientProviderBridge>
                <ErrorBoundary>
                  <CssBaseline />
                  <LokaliseInContextEditor
                    enabled={editorEnabled}
                    projectId={LOKALISE_PROJECT_ID}
                  />
                  <AppRoutes />
                </ErrorBoundary>
                <ReactQueryDevtools />
              </QueryClientProviderBridge>
            </ErrorHandlerProvider>
          </SnackbarProvider>
        </MsalAccountProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
}

export default App;
